// client/src/neetjee_guru/components/libraryManagement/IssueBook.jsx
import React, { Component } from "react";
import { Form, Button, Row, Col, ListGroup, Card, Alert, Spinner, Badge, FormControl } from "react-bootstrap";
import * as Cookies from "es-cookie";
import { GET_NEW_EDUCATIONAL_STUDENTS, GET_EMPLOYEES, GET_BOOKS, GET_LIBRARY_SETTINGS, GET_STUDENT_ACTIVE_LOANS } from "./queries";
import { ISSUE_MULTIPLE_BOOKS } from "./mutations";

class IssueBook extends Component {
  constructor(props) {
    super(props);
    this.state = {
      borrowerType: (props.selectedBorrower && props.selectedBorrower.type === "Employee") ? "Employee" : "Student",
      allStudents: [],
      allEmployees: [],
      filteredBorrowers: [],
      
      // Active selection
      studentQuery: "",
      loadingStudents: false,
      
      // Book catalog search
      allBooks: [],
      bookQuery: "",
      booksResult: [],
      selectedBook: null,
      selectedCopy: null,
      loanType: "Standard",
      loadingBooks: false,
      
      // Cart Management
      cart: [], // Array of { id, accession_no, book_title, book_id }
      addingToCart: false,   // loading state while server quota is being verified
      cartError: null,       // inline error shown below the Add to Cart button
      
      // Live quota tracking — seeded from props then refreshed on every addToCart
      liveActiveLoans: [],
      
      // Circulation details
      issueDate: new Date().toISOString().split("T")[0],
      dueDate: "",
      issuing: false,
      issueSuccess: null,
      issueError: null,

      // Settings tracking
      librarySettings: null,
    };
  }

  componentDidMount() {
    this.loadSettings();
    this.loadBorrowers();
    this.loadBooks();
  }

  componentDidUpdate(prevProps) {
    // Seed liveActiveLoans from parent whenever the parent's activeLoans prop updates
    // (happens when borrower is selected or after a successful checkout refreshLoans call)
    const prevLoans = prevProps.activeLoans || [];
    const nextLoans = this.props.activeLoans || [];
    if (prevLoans !== nextLoans) {
      this.setState({ liveActiveLoans: nextLoans });
    }
  }

  loadSettings = async () => {
    const { client } = this.props;
    const instId = parseInt(Cookies.get("institute_id") || Cookies.get("institutionid") || "0");
    const branchId = parseInt(Cookies.get("branch_id") || Cookies.get("branchid") || "0");

    try {
      const { data } = await client.query({
        query: GET_LIBRARY_SETTINGS,
        variables: { institute_id: instId, branch_id: branchId },
        fetchPolicy: "network-only"
      });
      if (data && data.getLibrarySettings) {
        this.setState({ librarySettings: data.getLibrarySettings }, this.calculateDueDate);
      }
    } catch (err) {
      console.error("Error loading library settings:", err);
    }
  };

  calculateDueDate = () => {
    const { loanType, librarySettings } = this.state;
    const due = new Date();
    const standardDays = librarySettings ? librarySettings.max_borrow_days : 14;
    const semesterDays = librarySettings ? librarySettings.semester_borrow_days : 120;
    const days = loanType === "Standard" ? standardDays : semesterDays;
    due.setDate(due.getDate() + days);
    this.setState({ dueDate: due.toISOString().split("T")[0] });
  };

  loadBooks = async () => {
    const { client } = this.props;
    const instId = parseInt(Cookies.get("institute_id") || Cookies.get("institutionid") || "0");
    const branchId = parseInt(Cookies.get("branch_id") || Cookies.get("branchid") || "0");

    try {
      this.setState({ loadingBooks: true });
      const { data } = await client.query({
        query: GET_BOOKS,
        variables: { institute_id: instId, branch_id: branchId },
        fetchPolicy: "network-only"
      });
      this.setState({ allBooks: data.getBooks || [], loadingBooks: false }, this.searchBooksLocal);
    } catch (err) {
      console.error("Error loading books catalog:", err);
      this.setState({ loadingBooks: false });
    }
  };

  loadBorrowers = async () => {
    const { client } = this.props;
    const instId = parseInt(Cookies.get("institute_id") || Cookies.get("institutionid") || "0");
    const branchId = parseInt(Cookies.get("branch_id") || Cookies.get("branchid") || "0");

    this.setState({ loadingStudents: true, issueError: null });

    let studentsList = [];
    try {
      const studentsData = await client.query({
        query: GET_NEW_EDUCATIONAL_STUDENTS,
        variables: { institution_id: instId, branch_id: String(branchId) },
        fetchPolicy: "cache-first",
      });
      // Filter out any UNION rows where edu_student_id was NULL (online-only users)
      // parseInt(null) = NaN — these can't be used as borrower_id and must be excluded
      studentsList = (studentsData.data.getNewEducationalStudents || [])
        .filter(s => s.student_id != null && !isNaN(parseInt(s.student_id)))
        .map(s => ({
          id: parseInt(s.student_id),
          name: s.student_name || "(No name)",
          roll_no: s.roll_no || "",
          admission_no: s.admission_no || "",
          code: s.admission_no || s.roll_no || String(s.student_id),
          details: `Class: ${s.class_name || ""} (${s.section_name || ""}) | Adm No: ${s.admission_no || ""}`,
          type: "Student",
        }));
    } catch (err) {
      console.warn("getNewEducationalStudents query failed, falling back to dummy students:", err);
    }

    if (studentsList.length === 0) {
      studentsList = [
        { id: 101, name: "Aravind Swamy", roll_no: "CSE-23-01", admission_no: "ADM-2023-0101", code: "ADM-2023-0101", details: "Class: B.Tech III Yr (A) | Adm No: ADM-2023-0101", type: "Student" },
        { id: 102, name: "Priyanka Reddy", roll_no: "CSE-23-02", admission_no: "ADM-2023-0102", code: "ADM-2023-0102", details: "Class: B.Tech III Yr (A) | Adm No: ADM-2023-0102", type: "Student" },
        { id: 103, name: "Amit Sharma", roll_no: "ECE-23-45", admission_no: "ADM-2023-0103", code: "ADM-2023-0103", details: "Class: B.Tech III Yr (B) | Adm No: ADM-2023-0103", type: "Student" }
      ];
    }

    let employeesList = [];
    try {
      const employeesData = await client.query({
        query: GET_EMPLOYEES,
        variables: { institute_id: instId, branch_id: branchId },
        fetchPolicy: "cache-first",
      });
      employeesList = (employeesData.data.getEmployees || [])
        .filter(e => e.employee_id != null && !isNaN(parseInt(e.employee_id)))
        .map(e => ({
          id: parseInt(e.employee_id),
          name: e.employee_name || "(No name)",
          code: String(e.employee_id),
          details: `Employee ID: ${e.employee_id}`,
          type: "Employee",
        }));
    } catch (err) {
      console.warn("getEmployees query failed, falling back to dummy employees:", err);
    }

    if (employeesList.length === 0) {
      employeesList = [
        { id: 701, name: "Prof. Suresh R.", code: "201", details: "Department: Computer Science", type: "Employee" },
        { id: 702, name: "Dr. Anjali Patil", code: "202", details: "Department: Mathematics", type: "Employee" }
      ];
    }

    this.setState({
      allStudents: studentsList,
      allEmployees: employeesList,
      loadingStudents: false,
    }, this.searchStudents);
  };

  handleFieldChange = (e) => {
    const { name, value } = e.target;
    this.setState({ [name]: value }, () => {
      if (name === "studentQuery") {
        this.searchStudents();
      }
      if (name === "bookQuery") {
        this.searchBooksLocal();
      }
      if (name === "loanType") {
        this.calculateDueDate();
      }
    });
  };

  searchStudents = () => {
    const { studentQuery, borrowerType, allStudents, allEmployees } = this.state;
    if (!studentQuery.trim()) {
      this.setState({ filteredBorrowers: [] });
      return;
    }

    const list = borrowerType === "Student" ? allStudents : allEmployees;
    const query = studentQuery.toLowerCase().trim();
    const filtered = list.filter(b => {
      // Guard: skip any entry with a missing name to prevent toLowerCase crash
      if (!b.name) return false;
      if (b.type === "Student") {
        return (
          b.name.toLowerCase().includes(query) ||
          (b.roll_no && b.roll_no.toLowerCase().includes(query)) ||
          (b.admission_no && b.admission_no.toLowerCase().includes(query))
        );
      } else {
        return (
          b.name.toLowerCase().includes(query) ||
          (b.code && b.code.toLowerCase().includes(query))
        );
      }
    });
    this.setState({ filteredBorrowers: filtered });
  };

  searchBooksLocal = () => {
    const { bookQuery, allBooks } = this.state;
    const query = bookQuery.toLowerCase().trim();

    const availableBooks = allBooks.filter(b => b.copies && b.copies.some(c => c.status === "available"));

    if (!query) {
      this.setState({ booksResult: availableBooks });
      return;
    }

    const filtered = availableBooks.filter(b => 
      b.title.toLowerCase().includes(query) ||
      b.author.toLowerCase().includes(query) ||
      b.category.toLowerCase().includes(query)
    );
    this.setState({ booksResult: filtered });
  };

  selectStudent = (student) => {
    this.setState({ studentQuery: "", filteredBorrowers: [], borrowerType: student.type === "Employee" ? "Employee" : "Student" });
    if (this.props.setSelectedBorrower) {
      this.props.setSelectedBorrower(student);
    }
  };

  selectBook = (book) => {
    const availableCopies = book.copies ? book.copies.filter(c => c.status === "available") : [];
    this.setState({
      selectedBook: book,
      selectedCopy: availableCopies.length > 0 ? availableCopies[0] : null,
      bookQuery: ""
    });
  };

  addToCart = async () => {
    const { selectedBook, selectedCopy, cart, loanType, librarySettings } = this.state;
    const selectedStudent = this.props.selectedBorrower;

    if (!selectedBook || !selectedCopy) return;

    if (cart.some(item => item.id === selectedCopy.id)) {
      this.setState({ cartError: "This specific book copy is already in your checkout cart." });
      return;
    }

    // ── Live Quota Check ─────────────────────────────────────────────────────────
    // Fetch current active loans directly from the server (network-only bypasses
    // the Apollo cache) so we always validate against real DB state, not stale props.
    // This prevents the UI showing a wrong quota if another session issued a book
    // to the same patron in the background.
    this.setState({ addingToCart: true, cartError: null });

    try {
      const { client } = this.props;
      const instId = parseInt(Cookies.get("institute_id") || Cookies.get("institutionid") || "0");
      const branchId = parseInt(Cookies.get("branch_id") || Cookies.get("branchid") || "0");

      const { data } = await client.query({
        query: GET_STUDENT_ACTIVE_LOANS,
        variables: {
          borrower_id: selectedStudent.id,
          borrower_type: selectedStudent.type,
          institute_id: instId,
          branch_id: branchId,
        },
        fetchPolicy: "network-only",  // always go to the server, never serve from cache
      });

      const freshLoans = data.getStudentActiveLoans || [];

      // Sync local live state so the quota gauge reflects reality immediately
      this.setState({ liveActiveLoans: freshLoans });

      const freshActiveOfThisType = freshLoans.filter(
        l => loanType === "Book Bank" ? l.loan_type === "Book Bank" : l.loan_type !== "Book Bank"
      ).length;

      const limit = loanType === "Book Bank"
        ? (librarySettings ? librarySettings.max_semester_books : 4)
        : (librarySettings ? librarySettings.max_books : 3);

      const totalAfterAdd = freshActiveOfThisType + cart.length + 1;
      if (totalAfterAdd > limit) {
        const errorMsg = `Quota exceeded! ${freshActiveOfThisType} active + ${cart.length} in cart + 1 new = ${totalAfterAdd} would exceed the ${loanType} limit of ${limit}.`;
        this.setState({
          addingToCart: false,
          cartError: errorMsg,
        });
        if (this.props.showToast) {
          this.props.showToast(`Cannot Add: ${errorMsg}`, "error");
        }
        return;
      }

      const cartItem = {
        id: selectedCopy.id,
        accession_no: selectedCopy.accession_no,
        book_title: selectedBook.title,
        book_id: selectedBook.id,
        rack_location: selectedCopy.rack_location || "Unassigned"
      };

      this.setState({
        cart: [...cart, cartItem],
        selectedBook: null,
        selectedCopy: null,
        addingToCart: false,
        cartError: null,
      }, this.searchBooksLocal);

    } catch (err) {
      console.error("Error verifying quota before addToCart:", err);
      this.setState({
        addingToCart: false,
        cartError: "Failed to verify quota from server: " + err.message,
      });
    }
  };

  removeFromCart = (copyId) => {
    this.setState({
      cart: this.state.cart.filter(item => item.id !== copyId)
    });
  };

  clearCart = () => {
    this.setState({ cart: [] });
  };

  handleBatchCheckout = async (e) => {
    e.preventDefault();
    const selectedStudent = this.props.selectedBorrower;
    const { cart, loanType } = this.state;

    if (!selectedStudent || cart.length === 0) {
      this.setState({ issueError: "Checkout aborted: Empty cart or no borrower selected." });
      return;
    }

    const { client } = this.props;
    const instId = parseInt(Cookies.get("institute_id") || Cookies.get("institutionid") || "0");
    const branchId = parseInt(Cookies.get("branch_id") || Cookies.get("branchid") || "0");

    try {
      this.setState({ issuing: true, issueError: null });
      const { data } = await client.mutate({
        mutation: ISSUE_MULTIPLE_BOOKS,
        variables: {
          borrower_type: selectedStudent.type,
          borrower_id: selectedStudent.id,
          copy_ids: cart.map(item => parseInt(item.id)),
          loan_type: loanType,
          institute_id: instId,
          branch_id: branchId,
        },
      });

      const count = data.issueMultipleBooks.length;
      this.setState({
        issuing: false,
        issueSuccess: `Successfully checked out ${count} books to ${selectedStudent.name}! Due date: ${data.issueMultipleBooks[0].due_date}`,
        cart: [],
        selectedBook: null,
        selectedCopy: null,
      });

      await this.loadBooks();
      if (this.props.refreshLoans) {
        this.props.refreshLoans();
      }
      if (this.props.showToast) {
        this.props.showToast(`Successfully checked out ${count} books to ${selectedStudent.name}!`, "success");
      }
    } catch (err) {
      console.error("Error issuing multiple books:", err);
      this.setState({ issuing: false, issueError: err.message });
    }
  };

  switchBorrowerType = (type) => {
    this.setState({
      borrowerType: type,
      studentQuery: "",
      filteredBorrowers: [],
      cart: [],
      selectedBook: null,
      selectedCopy: null,
      issueSuccess: null,
      issueError: null
    });
    if (this.props.setSelectedBorrower) {
      this.props.setSelectedBorrower(null);
    }
  };

  resetFlow = () => {
    this.setState({
      selectedBook: null,
      selectedCopy: null,
      filteredBorrowers: [],
      studentQuery: "",
      bookQuery: "",
      cart: [],
      issueSuccess: null,
      issueError: null,
    }, () => {
      this.calculateDueDate();
      this.loadBooks();
      if (this.props.setSelectedBorrower) {
        this.props.setSelectedBorrower(null);
      }
    });
  };

  render() {
    const {
      borrowerType,
      studentQuery,
      filteredBorrowers,
      bookQuery,
      booksResult,
      selectedBook,
      selectedCopy,
      loanType,
      dueDate,
      loadingStudents,
      loadingBooks,
      issuing,
      issueSuccess,
      issueError,
      librarySettings,
      cart,
      addingToCart,
      cartError,
      liveActiveLoans,
    } = this.state;

    const selectedStudent = this.props.selectedBorrower;
    // Use liveActiveLoans (freshly fetched on every addToCart) for gauge display,
    // falling back to props.activeLoans only on the initial render before first add.
    const activeLoans = liveActiveLoans.length > 0 ? liveActiveLoans : (this.props.activeLoans || []);
    const loadingLoans = this.props.loadingLoans || false;

    // Full Screen Centered layout if borrower not selected
    if (!selectedStudent) {
      return (
        <div className="pb-5">
          {issueError && <Alert variant="danger" className="mb-4">{issueError}</Alert>}
          {issueSuccess && (
            <Alert variant="success" className="mb-4 d-flex justify-content-between align-items-center" style={{ borderRadius: 8 }}>
              <div>
                <i className="fal fa-check-circle fa-lg mr-2" style={{ color: "#10b981", marginRight: 8 }} />
                <strong>Checkout Success:</strong> {issueSuccess}
              </div>
              <Button size="sm" variant="outline-success" onClick={() => this.setState({ issueSuccess: null })} style={{ fontWeight: 700 }}>
                Dismiss
              </Button>
            </Alert>
          )}

          <Row className="justify-content-center">
            <Col md={7} lg={5}>
              <Card className="border-0 shadow-sm" style={{ borderRadius: 16, marginTop: "24px" }}>
                <Card.Header style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0", padding: "20px 24px" }}>
                  <h6 className="mb-0 text-center" style={{ fontWeight: 800, color: "#1e293b", fontSize: 15 }}>
                    Identify Patrons / Borrowers
                  </h6>
                </Card.Header>
                <Card.Body className="p-4">
                  {/* Borrower Type Selection */}
                  <div className="d-flex mb-4" style={{ gap: 8 }}>
                    <Button
                      size="sm"
                      style={{
                        background: borrowerType === "Student" ? "#4f46e5" : "#f1f5f9",
                        border: "none",
                        color: borrowerType === "Student" ? "#fff" : "#475569",
                        fontWeight: 700,
                        padding: "10px 16px",
                        borderRadius: 8,
                        flex: 1
                      }}
                      onClick={() => this.switchBorrowerType("Student")}
                    >
                      <i className="fal fa-graduation-cap mr-2" style={{ marginRight: 6 }} /> Student
                    </Button>
                    <Button
                      size="sm"
                      style={{
                        background: borrowerType === "Employee" ? "#4f46e5" : "#f1f5f9",
                        border: "none",
                        color: borrowerType === "Employee" ? "#fff" : "#475569",
                        fontWeight: 700,
                        padding: "10px 16px",
                        borderRadius: 8,
                        flex: 1
                      }}
                      onClick={() => this.switchBorrowerType("Employee")}
                    >
                      <i className="fal fa-user-briefcase mr-2" style={{ marginRight: 6 }} /> Staff
                    </Button>
                  </div>

                  <Form.Group className="mb-4">
                    <Form.Label style={{ fontSize: 11, fontWeight: 800, color: "#64748b" }} className="text-uppercase">Search Borrower Name or Code</Form.Label>
                    <Form.Control
                      type="text"
                      name="studentQuery"
                      value={studentQuery}
                      onChange={this.handleFieldChange}
                      placeholder="Type name, roll or admission code..."
                      style={{ height: 44, fontSize: 13, borderRadius: 8 }}
                      autoComplete="off"
                    />
                  </Form.Group>

                  {loadingStudents ? (
                    <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>
                  ) : (
                    <ListGroup style={{ maxHeight: 250, overflowY: "auto" }}>
                      {filteredBorrowers.map((st) => (
                        <ListGroup.Item
                          key={st.id}
                          action
                          onClick={() => this.selectStudent(st)}
                          style={{ border: "1px solid #f1f5f9", borderRadius: 8, marginBottom: 8, padding: "12px 16px" }}
                          className="d-flex justify-content-between align-items-center"
                        >
                          <div>
                            <div style={{ fontWeight: 700, color: "#1e293b", fontSize: 13 }}>{st.name}</div>
                            <small className="text-muted" style={{ fontSize: 11 }}>Code: {st.code}</small>
                          </div>
                          <Badge variant="primary" style={{ background: "#e0f2fe", color: "#0369a1", fontSize: 10, padding: "5px 8px" }}>Select</Badge>
                        </ListGroup.Item>
                      ))}
                      {filteredBorrowers.length === 0 && studentQuery.trim() && (
                        <div className="text-center py-4 text-muted" style={{ fontSize: 12 }}>No matching borrower profiles found.</div>
                      )}
                    </ListGroup>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>
      );
    }

    // 3-Column Layout when borrower is selected
    return (
      <div className="pb-5">
        {issueError && <Alert variant="danger" className="mb-4">{issueError}</Alert>}
        {issueSuccess && (
          <Alert variant="success" className="mb-4 d-flex justify-content-between align-items-center" style={{ borderRadius: 8 }}>
            <div>
              <i className="fal fa-check-circle fa-lg mr-2" style={{ color: "#10b981", marginRight: 8 }} />
              <strong>Checkout Success:</strong> {issueSuccess}
            </div>
            <Button size="sm" variant="outline-success" onClick={() => this.setState({ issueSuccess: null })} style={{ fontWeight: 700 }}>
              Dismiss
            </Button>
          </Alert>
        )}

        <Row style={{ gap: "0" }}>
          {/* COLUMN 1: Active Borrower Profile Card (Width: 3) */}
          <Col lg={3} className="mb-4">
            <Card className="border-0 shadow-sm" style={{ borderRadius: 12, height: "100%" }}>
              <Card.Header style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0", padding: "14px 18px" }} className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0" style={{ fontWeight: 800, color: "#1e293b", fontSize: 13 }}>Borrower Info</h6>
                <Button 
                  variant="link" 
                  onClick={this.resetFlow} 
                  style={{ color: "#ef4444", fontSize: 12, fontWeight: 700, padding: 0 }}
                >
                  Change
                </Button>
              </Card.Header>
              <Card.Body className="p-3">
                <div className="p-3 border rounded bg-light" style={{ borderRadius: 10 }}>
                  <small className="text-muted text-uppercase" style={{ fontSize: 9, fontWeight: 700 }}>Active Profile</small>
                  <h5 className="mt-1 mb-1" style={{ fontWeight: 800, color: "#0f172a", fontSize: 15 }}>{selectedStudent.name}</h5>
                  <div style={{ fontSize: 12, color: "#475569" }} className="mb-2">Code: <strong style={{ color: "#0f172a" }}>{selectedStudent.code}</strong></div>
                  
                  <Badge variant="info" style={{ padding: "4px 8px", background: "#e0f2fe", color: "#0369a1", fontWeight: 700, fontSize: 10 }}>
                    {selectedStudent.type}
                  </Badge>
                  <div className="mt-2 text-muted" style={{ fontSize: 11 }}>
                    <i className="fal fa-info-circle mr-1" style={{ marginRight: 4 }} /> {selectedStudent.details}
                  </div>

                  {loadingLoans ? (
                    <div className="text-muted mt-3" style={{ fontSize: 11 }}>
                      <Spinner animation="border" size="sm" style={{ marginRight: 6, width: 12, height: 12 }} /> 
                      Verifying limits...
                    </div>
                  ) : (() => {
                    const standardActive = activeLoans.filter(l => l.loan_type === "Standard").length;
                    const standardMax = librarySettings ? librarySettings.max_books : 3;
                    const standardPercent = Math.min(((standardActive + (loanType === "Standard" ? cart.length : 0)) / standardMax) * 100, 100);

                    const semesterActive = activeLoans.filter(l => l.loan_type === "Book Bank").length;
                    const semesterMax = librarySettings ? librarySettings.max_semester_books : 4;
                    const semesterPercent = Math.min(((semesterActive + (loanType === "Book Bank" ? cart.length : 0)) / semesterMax) * 100, 100);

                    return (
                      <div className="mt-3 p-2 bg-white rounded border" style={{ fontSize: 11, borderColor: "#e2e8f0" }}>
                        <div className="mb-2">
                          <div className="d-flex justify-content-between mb-1">
                            <span style={{ color: "#64748b", fontWeight: 600 }}>Standard Scheme:</span>
                            <span style={{ fontWeight: 700, color: (standardActive + (loanType === "Standard" ? cart.length : 0)) >= standardMax ? "#ef4444" : "#4f46e5" }}>
                              {standardActive} {loanType === "Standard" && cart.length > 0 && `(+${cart.length})`} / {standardMax}
                            </span>
                          </div>
                          <div style={{ background: "#e2e8f0", borderRadius: 4, height: 6, overflow: "hidden" }}>
                            <div style={{ background: (standardActive + (loanType === "Standard" ? cart.length : 0)) >= standardMax ? "#ef4444" : "#4f46e5", width: `${standardPercent}%`, height: "100%", borderRadius: 4 }} />
                          </div>
                        </div>
                        <div>
                          <div className="d-flex justify-content-between mb-1">
                            <span style={{ color: "#64748b", fontWeight: 600 }}>Book Bank Scheme:</span>
                            <span style={{ fontWeight: 700, color: (semesterActive + (loanType === "Book Bank" ? cart.length : 0)) >= semesterMax ? "#ef4444" : "#10b981" }}>
                              {semesterActive} {loanType === "Book Bank" && cart.length > 0 && `(+${cart.length})`} / {semesterMax}
                            </span>
                          </div>
                          <div style={{ background: "#e2e8f0", borderRadius: 4, height: 6, overflow: "hidden" }}>
                            <div style={{ background: (semesterActive + (loanType === "Book Bank" ? cart.length : 0)) >= semesterMax ? "#ef4444" : "#10b981", width: `${semesterPercent}%`, height: "100%", borderRadius: 4 }} />
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* COLUMN 2: Catalog Book Finder & Copies Select Grid (Width: 6) */}
          <Col lg={6} className="mb-4 px-lg-3">
            <Card className="border-0 shadow-sm" style={{ borderRadius: 12, height: "100%" }}>
              <Card.Header style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0", padding: "14px 18px" }}>
                <h6 className="mb-0" style={{ fontWeight: 800, color: "#1e293b", fontSize: 13 }}>Find Catalog Titles</h6>
              </Card.Header>
              <Card.Body className="p-3">
                {!selectedBook ? (
                  <div>
                    <Form.Group className="mb-3">
                      <FormControl
                        type="text"
                        name="bookQuery"
                        value={bookQuery}
                        onChange={this.handleFieldChange}
                        placeholder="Search books by title, author, or category..."
                        style={{ height: 40, fontSize: 13, borderRadius: 8 }}
                      />
                    </Form.Group>

                    {loadingBooks ? (
                      <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>
                    ) : (
                      <ListGroup style={{ maxHeight: 380, overflowY: "auto" }}>
                        {booksResult.map((bk) => (
                          <ListGroup.Item
                            key={bk.id}
                            action
                            onClick={() => this.selectBook(bk)}
                            style={{ border: "1px solid #f1f5f9", borderRadius: 8, marginBottom: 8, padding: "10px 14px" }}
                            className="d-flex justify-content-between align-items-center"
                          >
                            <div style={{ flex: 1, marginRight: 10 }}>
                              <div style={{ fontWeight: 700, color: "#1e293b", fontSize: 13 }}>{bk.title}</div>
                              <small className="text-muted" style={{ fontSize: 11 }}>
                                Author: {bk.author} | Subject: <span style={{ color: "#166534" }}>{bk.subject || "General"}</span>
                              </small>
                            </div>
                            <Badge variant="success" style={{ background: "#dcfce7", color: "#15803d", padding: "5px 8px", borderRadius: 12, fontSize: 10 }}>
                              {bk.available_copies} copies
                            </Badge>
                          </ListGroup.Item>
                        ))}
                        {booksResult.length === 0 && (
                          <div className="text-center py-4 text-muted" style={{ fontSize: 12 }}>No available physical book copies matching the filter.</div>
                        )}
                      </ListGroup>
                    )}
                  </div>
                ) : (
                  // Select copies view
                  <div>
                    <div className="p-3 border rounded bg-white mb-3" style={{ position: "relative", borderRadius: 10 }}>
                      <Button 
                        variant="link" 
                        onClick={() => this.setState({ selectedBook: null, selectedCopy: null })} 
                        style={{ position: "absolute", top: 12, right: 12, color: "#ef4444", fontSize: 11, fontWeight: 700 }}
                      >
                        Change Title
                      </Button>
                      <small className="text-muted uppercase text-uppercase font-weight-bold" style={{ fontSize: 9, fontWeight: 700 }}>Chosen Title</small>
                      <h6 className="mt-1 mb-1" style={{ fontWeight: 800, color: "#0f172a", fontSize: 14 }}>{selectedBook.title}</h6>
                      <div style={{ fontSize: 11, color: "#64748b" }}>
                        Author: {selectedBook.author} | Subject: <strong style={{ color: "#166534" }}>{selectedBook.subject || "General"}</strong>
                      </div>
                    </div>

                    <Form.Label style={{ fontSize: 11, fontWeight: 700, color: "#64748b" }} className="mb-2 text-uppercase">Select Specific Barcode Copy *</Form.Label>
                    <Row style={{ gap: "8px 0" }}>
                      {selectedBook.copies && selectedBook.copies.filter(c => c.status === "available" && !cart.some(item => item.id === c.id)).map((copy) => {
                        const isSelected = selectedCopy && selectedCopy.id === copy.id;
                        return (
                          <Col md={6} key={copy.id}>
                            <div
                              onClick={() => this.setState({ selectedCopy: copy })}
                              style={{
                                border: isSelected ? "2px solid #4f46e5" : "1px solid #e2e8f0",
                                background: isSelected ? "#f5f3ff" : "#fff",
                                borderRadius: 8,
                                padding: "10px 14px",
                                cursor: "pointer",
                                transition: "all 0.2s ease"
                              }}
                              className="d-flex justify-content-between align-items-center"
                            >
                              <div>
                                <div style={{ fontWeight: 800, color: isSelected ? "#4f46e5" : "#1e293b", fontSize: 12 }}>{copy.accession_no}</div>
                                <small style={{ color: "#64748b", fontSize: 10 }}>Rack: {copy.rack_location || "Unassigned"}</small>
                              </div>
                              <i className={`fal ${isSelected ? "fa-dot-circle text-primary" : "fa-circle text-muted"}`} />
                            </div>
                          </Col>
                        );
                      })}
                      {(!selectedBook.copies || selectedBook.copies.filter(c => c.status === "available" && !cart.some(item => item.id === c.id)).length === 0) && (
                        <Col md={12}>
                          <div className="text-center p-3 text-danger border rounded bg-white" style={{ fontSize: 12 }}>
                            All available copies for this title are currently in checkout or already in your cart.
                          </div>
                        </Col>
                      )}
                    </Row>

                    <div className="d-flex justify-content-end gap-2 mt-4" style={{ gap: 8 }}>
                      <Button variant="light" size="sm" onClick={() => this.setState({ selectedBook: null, selectedCopy: null, cartError: null })} style={{ fontWeight: 600 }}>
                        Cancel
                      </Button>
                      <Button 
                        disabled={!selectedCopy || addingToCart} 
                        onClick={this.addToCart} 
                        size="sm"
                        style={{ background: "#4f46e5", border: "none", fontWeight: 700, minWidth: 110 }}
                      >
                        {addingToCart ? (
                          <><Spinner animation="border" size="sm" style={{ width: 12, height: 12, marginRight: 6 }} />Verifying...</>
                        ) : (
                          "Add to Cart"
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* COLUMN 3: Floating / Static Cart Sidebar (Width: 3) */}
          <Col lg={3} className="mb-4">
            <Card className="border-0 shadow-sm" style={{ borderRadius: 12, border: cart.length > 0 ? "2px solid #4f46e5" : "1px solid #e2e8f0", height: "100%" }}>
              <Card.Header style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0", padding: "14px 18px" }} className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0" style={{ fontWeight: 800, color: "#1e293b", fontSize: 13 }}>
                  Checkout Cart {cart.length > 0 && <Badge variant="primary" style={{ background: "#4f46e5", marginLeft: 6 }}>{cart.length}</Badge>}
                </h6>
                {cart.length > 0 && (
                  <Button variant="link" onClick={this.clearCart} style={{ color: "#ef4444", fontSize: 11, fontWeight: 700, padding: 0 }}>
                    Clear
                  </Button>
                )}
              </Card.Header>
              <Card.Body className="p-3 d-flex flex-column justify-content-between">
                <div>
                  {cartError && (
                    <Alert variant="danger" className="mb-3 p-2" style={{ fontSize: 12, borderRadius: 8 }} dismissible onClose={() => this.setState({ cartError: null })}>
                      <strong>Cannot Add:</strong> {cartError}
                    </Alert>
                  )}
                  {/* Configuration Area - Always Visible */}
                  <div className="mb-3 p-3 bg-light rounded border" style={{ borderColor: "#e2e8f0" }}>
                    <h6 style={{ fontSize: 11, fontWeight: 700, color: "#475569" }} className="text-uppercase mb-2">Checkout Rules</h6>
                    <Form.Group className="mb-2">
                      <Form.Label style={{ fontSize: 10, fontWeight: 700, color: "#64748b" }} className="text-uppercase mb-1">Loan Scheme Type</Form.Label>
                      <Form.Control
                          as="select"
                          name="loanType"
                          value={loanType}
                          onChange={this.handleFieldChange}
                          style={{ height: 34, fontSize: 12, padding: "0 8px", borderRadius: 6 }}
                      >
                        <option value="Standard">Standard (Normal)</option>
                        <option value="Book Bank">Semester (Book Bank)</option>
                      </Form.Control>
                    </Form.Group>

                    <Form.Group className="mb-0">
                      <Form.Label style={{ fontSize: 10, fontWeight: 700, color: "#64748b" }} className="text-uppercase mb-1">Due Date</Form.Label>
                      <Form.Control
                        type="date"
                        name="dueDate"
                        value={dueDate}
                        onChange={this.handleFieldChange}
                        style={{ height: 34, fontSize: 12, borderRadius: 6 }}
                      />
                    </Form.Group>
                  </div>

                  <h6 style={{ fontSize: 11, fontWeight: 700, color: "#475569" }} className="text-uppercase mb-2">Items to Issue</h6>
                  
                  {cart.length === 0 ? (
                    <div className="text-center py-4 text-muted rounded border border-dashed" style={{ fontSize: 12, borderStyle: "dashed", borderColor: "#cbd5e1", padding: 12 }}>
                      <i className="fal fa-shopping-basket fa-lg mb-2 text-muted d-block" />
                      No books in cart. Add copies from the catalog finder.
                    </div>
                  ) : (
                    <div style={{ maxHeight: 280, overflowY: "auto", paddingRight: 4 }} className="mb-3">
                      {cart.map((item) => (
                        <div 
                          key={item.id} 
                          className="d-flex justify-content-between align-items-center p-2 mb-2 bg-white rounded border"
                          style={{ 
                            borderLeft: "3px solid #6366f1", 
                            borderColor: "#e2e8f0",
                            boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                            borderRadius: 6
                          }}
                        >
                          <div style={{ maxWidth: "80%" }}>
                            <div style={{ fontWeight: 800, color: "#1e293b", fontSize: 12, textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>
                              {item.book_title}
                            </div>
                            <div className="d-flex align-items-center mt-1" style={{ gap: 8 }}>
                              <Badge style={{ background: "#f1f5f9", color: "#475569", fontSize: 9, fontWeight: 700, borderRadius: 4, padding: "3px 6px" }}>
                                {item.accession_no}
                              </Badge>
                              <small className="text-muted" style={{ fontSize: 9 }}>
                                <i className="fal fa-map-marker-alt" style={{ marginRight: 3 }} /> {item.rack_location || "Unassigned"}
                              </small>
                            </div>
                          </div>
                          <Button 
                            variant="light" 
                            onClick={() => this.removeFromCart(item.id)} 
                            style={{ 
                              color: "#ef4444", 
                              fontSize: 10, 
                              padding: "4px 8px", 
                              borderRadius: 6,
                              border: "none",
                              background: "#fef2f2"
                            }}
                            className="btn-sm"
                          >
                            <i className="fal fa-trash-alt" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {cart.length > 0 && (
                  <Form onSubmit={this.handleBatchCheckout} className="mt-3">
                    <Button 
                      type="submit" 
                      disabled={issuing} 
                      className="w-100"
                      style={{ background: "#4f46e5", border: "none", fontWeight: 800, fontSize: 13, height: 38, borderRadius: 8 }}
                    >
                      {issuing ? "Issuing..." : `Checkout ${cart.length} Books`}
                    </Button>
                  </Form>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    );
  }
}

export default IssueBook;
